import { useEffect, useMemo, useState } from "react";

import type { RepositoryIndexingRunFailure } from "../types";

import { fetchRepositoryIndexingRunFailures } from "../api/sourceControl";
import { formatErrorMessage } from "../utils/errors";

interface FailureState {
  failures: RepositoryIndexingRunFailure[];
  loading: boolean;
  error: string | null;
}

const initialState: FailureState = {
  error: null,
  failures: [],
  loading: false,
};

export function useRepositoryIndexingFailures(
  runId: string | null,
  repositorySlug?: string | null,
) {
  const [state, setState] = useState<FailureState>(initialState);

  useEffect(() => {
    if (!runId) {
      setState(initialState);
      return;
    }

    let active = true;
    setState((current) => ({ ...current, error: null, loading: true }));

    fetchRepositoryIndexingRunFailures(runId)
      .then((failures) => {
        if (!active) {
          return;
        }
        setState({ error: null, failures, loading: false });
      })
      .catch((error: unknown) => {
        if (!active) {
          return;
        }
        setState({
          error: formatErrorMessage(error),
          failures: [],
          loading: false,
        });
      });

    return () => {
      active = false;
    };
  }, [runId]);

  const filteredFailures = useMemo(() => {
    if (!repositorySlug) {
      return state.failures;
    }
    return state.failures.filter(
      (failure) => failure.repository_slug === repositorySlug,
    );
  }, [repositorySlug, state.failures]);

  return {
    error: state.error,
    failures: filteredFailures,
    loading: state.loading,
  };
}
